import { useLayoutEffect, useRef, useState } from "react";

import type { TickerItem } from "../types";

interface LiveTickerProps {
  items: TickerItem[];
}

/** Desired scroll speed in pixels per second. */
const TICKER_SPEED_PX_PER_SEC = 60;

const TAG_STYLES: Record<NonNullable<TickerItem["tag"]>["variant"], string> = {
  work: "bg-emerald-500/20 text-emerald-300",
  approval: "bg-yellow-400/20 text-yellow-300",
  lost: "bg-red-500/20 text-red-300",
};

/**
 * Full-width dark scrolling ticker bar that displays live activity items.
 * Items scroll continuously left and pause on hover. The content is
 * duplicated as needed so the animation loops seamlessly regardless of
 * how many items are displayed.
 * @param props - The ticker items to display
 * @returns A dark banner with a LIVE label and scrolling ticker items
 */
export function LiveTicker({ items }: LiveTickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const halfRef = useRef<HTMLDivElement>(null);
  const [config, setConfig] = useState({ repeatCount: 1, duration: 30 });

  useLayoutEffect(() => {
    const container = containerRef.current;
    const half = halfRef.current;
    if (!container || !half) {
      return;
    }

    const containerWidth = container.offsetWidth;
    const halfWidth = half.scrollWidth;
    const singleSetWidth = halfWidth / config.repeatCount;

    if (singleSetWidth <= 0) {
      return;
    }

    const repeatCount = Math.max(1, Math.ceil(containerWidth / singleSetWidth));
    const newHalfWidth = singleSetWidth * repeatCount;
    const duration = Math.max(10, newHalfWidth / TICKER_SPEED_PX_PER_SEC);

    if (
      repeatCount !== config.repeatCount ||
      Math.abs(duration - config.duration) > 1
    ) {
      setConfig({ repeatCount, duration });
    }
  }, [items, config.repeatCount, config.duration]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="sticky top-0 z-50 flex h-10 items-stretch overflow-hidden border-b border-border bg-neutral-950 text-white">
      {/* Scrolling track */}
      <div
        ref={containerRef}
        className="flex flex-1 items-center overflow-hidden"
      >
        <div
          className="inline-flex items-center whitespace-nowrap font-mono text-[12.5px] hover:[animation-play-state:paused]"
          style={{
            animation: `ticker ${config.duration}s linear infinite`,
          }}
        >
          <div ref={halfRef} className="inline-flex shrink-0 items-center">
            {Array.from({ length: config.repeatCount }, (_, i) => (
              <TickerContent key={i} items={items} />
            ))}
          </div>
          <div className="inline-flex shrink-0 items-center">
            {Array.from({ length: config.repeatCount }, (_, i) => (
              <TickerContent key={i} items={items} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a single pass of all ticker items (used within each half of the track).
 * @param props - The ticker items to render
 * @returns A fragment of ticker item spans
 */
function TickerContent({ items }: { items: TickerItem[] }) {
  return (
    <>
      {items.map((item, index) => (
        <span
          key={index}
          className="inline-flex items-center gap-[9px] px-[26px] text-white/70"
        >
          <span className="text-white/30">●</span>
          {item.tag && (
            <span
              className={`rounded px-1.5 py-px font-mono text-[10px] font-semibold tracking-[0.5px] ${TAG_STYLES[item.tag.variant]}`}
            >
              {item.tag.text}
            </span>
          )}
          {item.bold && <b className="font-semibold text-white">{item.bold}</b>}
          <span>{item.text}</span>
        </span>
      ))}
    </>
  );
}
